/**
 * PolicyTableContainer
 * Shared container layout for policy tables (Dashboard and Tenant views)
 * Includes header, tabs, and common styling
 */

import React from 'react';
import {
  Text,
  TabList,
  Tab,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components';
import { PolicyTabType } from '../types';
import TenantPrefixBox from '../../common/TenantPrefixBox';

interface PolicyTableContainerProps {
  /** Optional title prefix (e.g., "Policies for TenantName") */
  title?: string;
  /** Subtitle text */
  subtitle?: string;
  /** Currently selected tab */
  selectedTab: PolicyTabType;
  /** Tab change handler */
  onTabChange: (tab: PolicyTabType) => void;
  /** DLP policies count for tab label */
  dlpCount: number;
  /** Auto Label policies count for tab label */
  autoLabelCount: number;
  /** Tenant label prefix. Pass to show the prefix pill beside the subtitle (em dash when empty). Omit entirely on dashboard/multi-tenant views to hide the pill. */
  prefix?: string;
  /** Error message to display */
  error?: string | null;
  /** Child content (filter bar, table, dialogs) */
  children: React.ReactNode;
}

export function PolicyTableContainer({
  title = 'Policies',
  subtitle = 'Manage policies for this tenant',
  selectedTab,
  onTabChange,
  dlpCount,
  autoLabelCount,
  prefix,
  error,
  children,
}: PolicyTableContainerProps) {
  const handleTabSelect = (_event: any, data: any) => {
    onTabChange(data.value as PolicyTabType);
  };

  return (
    <div
      style={{
        background: 'white',
        border: '1px solid #e5e5e5',
        borderRadius: 8,
        padding: '0 24px 24px 24px',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '24px 0 20px 0',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>{title}</h2>
          <Text size={300} style={{ color: '#6b7280' }}>
            {subtitle}
          </Text>
          {prefix !== undefined && (
            <div style={{ marginTop: 8 }}>
              <TenantPrefixBox prefix={prefix} />
            </div>
          )}
        </div>
        <div />
      </div>

      <div style={{ height: 1, background: '#e5e5e5', margin: '0 -24px 24px -24px' }} />

      {/* Error Message */}
      {error && (
        <div style={{ marginBottom: 16 }}>
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>Error loading policies</MessageBarTitle>
              {error}
            </MessageBarBody>
          </MessageBar>
        </div>
      )}

      {/* Tabs */}
      <div style={{ marginBottom: 24 }}>
        <TabList selectedValue={selectedTab} onTabSelect={handleTabSelect}>
          <Tab value="DLP">DLP Policies ({dlpCount})</Tab>
          <Tab value="AutoLabel">Auto Label Policies ({autoLabelCount})</Tab>
        </TabList>
      </div>

      {children}
    </div>
  );
}
